/**
 * Turns the media list into the rows of a spreadsheet.
 *
 * Everything here is pure — rows in, strings out — so the values that land in the file can be tested
 * without a workbook, a browser download or a network call. The writing itself lives in excelWriter.js
 * and the fetching in dashboardCalls.js; this module is only the translation between them.
 *
 * The governing rule is that a cell in the file says what the cell on screen says. The table renders
 * some columns as pictures rather than text (a tape's type is drawn as a cartridge, its status resolved
 * from an id), and a spreadsheet cannot carry either, so both are exported as the text they are
 * derived from — which is also the text a reader wants to filter and pivot on.
 */
import {getFormattedProperty} from "./utils";
import {tapeStatusLabel} from "./tapeStatus";
import {TAPE_INGESTION_TABLE_COLUMNS} from "../component/processing/ingestionTableCustomization";

/**
 * The columns of the exported media sheet: exactly the columns of the tape table, in the same order.
 *
 * Shared with the table rather than restated, so the two cannot drift apart. A column added to the
 * table appears in the export with no second edit, which is the point — a report that quietly omits a
 * column the operator can see on screen is worse than no report.
 */
export const MEDIA_REPORT_COLUMNS = TAPE_INGESTION_TABLE_COLUMNS["Standard tapes"];

/**
 * The text for one cell of the exported sheet.
 *
 * @param {object} row - A media row as the SaaS returned it.
 * @param {object} column - One entry of a table column config: {name, mnemonic, ui_class, special_render}.
 * @returns {string} The cell text, never null — an absent value is an empty cell, not the word 'null'.
 */
export function mediaReportCellValue(row, column) {
    if (!row || !column) return '';

    /*
     * Tape Type. The table draws a cartridge here, but the picture is not extra information: the type
     * string is what selects the SVG and is then lettered onto it, so '8mm' on screen and '8mm' in the
     * file are the same fact — see getTapeIcon and DataTable's tape_photo branch. Exporting the string
     * is therefore not a downgrade of the cell, it is the cell with its packaging removed.
     */
    if (column.special_render === 'tape_photo') {
        return getFormattedProperty(row, column.mnemonic, column.ui_class ?? 'str') || '';
    }

    /*
     * Status. Resolved from status_id, not from the API's `status` string, because that string reads
     * 'Unknown' for every status past Uploaded — the view deriving it stops at 4. Exporting the raw
     * string would put a column of 'Unknown' into a customer's report for tapes whose status the portal
     * displays correctly one pixel away.
     */
    if (column.special_render === 'tape_status') {
        return tapeStatusLabel(row) || '';
    }

    // Everything else formats exactly as the table formats it: data volumes in ISO units, dates as
    // dd/Mon/yyyy. Same helper, same ui_class, so a column cannot read one way on screen and another
    // in the file.
    return getFormattedProperty(row, column.mnemonic, column.ui_class ?? 'str') || '';
}

/**
 * Which job a row belongs to, for a report that spans more than one of them.
 *
 * The selector's own label first, because that is the reference the reader ticked in the header and the
 * one they will match the file against. ArkBridge's own job description is the fallback, and the bare id
 * the last resort — a row whose job cannot be named must still say which job it was, or the column would
 * be worse than absent.
 *
 * @param {object} row - A media row as the SaaS returned it.
 * @param {object} [jobLabelsById] - {[jobId]: label} from the header's job list.
 * @returns {string}
 */
export function mediaReportJobLabel(row, jobLabelsById = {}) {
    const id = row?.job_id;
    const fromSelector = id === null || id === undefined ? undefined : jobLabelsById[id];
    const label = fromSelector ?? row?.job ?? id;
    return label === null || label === undefined ? '' : String(label);
}

/**
 * Builds the media sheet: a header row of column names, then one row of strings per tape.
 *
 * @param {object[]} rows - Media rows from the SaaS.
 * @param {object[]} [columns] - Column config; defaults to the tape table's.
 * @param {object} [options] - {jobLabelsById} — given, a leading 'Job' column names the job each row came
 *        from. Passed only when the report covers more than one job: a file scoped to a single job says so
 *        in its name, and a column repeating that job on every row of it is noise.
 * @returns {{headers: string[], rows: string[][]}}
 */
export function buildMediaReportSheet(rows, columns = MEDIA_REPORT_COLUMNS, {jobLabelsById} = {}) {
    /*
     * The job column is not part of the shared table config, and deliberately so.
     *
     * The rule above — a cell in the file says what the cell on screen says — is about the columns the
     * table has. This one answers a question the table never has to: the table is always looking at one
     * job's media, and a report that spans every onboarded job is a list of volsers with nothing to say
     * which job any of them belongs to. Added here rather than in TAPE_INGESTION_TABLE_COLUMNS so the
     * table does not grow a column of one repeated value.
     */
    const namesJobs = jobLabelsById !== undefined;
    return {
        headers: [...(namesJobs ? ['Job'] : []), ...columns.map(column => column.name)],
        // Driven off the column config, never off Object.keys(row): getFormattedProperty memoises onto
        // the row it formats (`__data_volume_formatted_as_...`), so a key-driven export would leak its
        // own cache into the file alongside every internal id the API happens to return.
        rows: (rows || []).map(row => [
            ...(namesJobs ? [mediaReportJobLabel(row, jobLabelsById)] : []),
            ...columns.map(column => mediaReportCellValue(row, column)),
        ]),
    };
}

/**
 * Builds the summary sheet from the counters displayed on the card.
 *
 * Included because the button belongs to the metrics card, and a reader who exports from it expects the
 * figures they were looking at to be in the file. Costs nothing: these are the numbers already on
 * screen, not a thirteenth request.
 *
 * Percentages are omitted deliberately — every one of them is derived from two counts that are both in
 * this sheet, and a stale-looking rounded percentage in a spreadsheet invites arithmetic that disagrees
 * with it.
 *
 * @param {object} metrics - The shape buildMediaMetrics returns.
 * @param {number} [totalMedia] - Total media in scope, from the list query's own count.
 * @param {number} [jobsInScope] - How many jobs the figures cover. Omitted, the row is absent, which is
 *        what a report of one job wants — its name already says which job it is.
 * @returns {{headers: string[], rows: Array<Array<string|number>>}}
 */
export function buildMetricsSummarySheet(metrics, totalMedia, jobsInScope) {
    const {inventory, virtualization, exceptions, destruction, shipment} = metrics || {};
    const entries = [
        // First, because it is the one figure that says how much of the portal the rest of the sheet is
        // about — a total of 158,004 means something different across one job than across forty.
        ['Jobs Reported On', jobsInScope],
        ['Total Media', totalMedia],
        ['Audit/Catalogued', inventory?.audited],
        ['Physical Stored', inventory?.storedOnSite],
        ['Virtualized/Uploaded', inventory?.virtualized],
        ['In Library', virtualization?.inLibrary],
        ['Reading', virtualization?.inProgressCount],
        ['Encrypted', exceptions?.encrypted],
        ['Read Failed', exceptions?.readFailed],
        ['Damaged', exceptions?.damaged],
        ['Physical Media Destroyed', destruction?.physicalMedia],
        ['Virtual Media Destroyed', destruction?.virtualMedia],
        ['Shipment To TapeArk', shipment?.toTapeArk],
        ['Shipment Return', shipment?.return],
        ['Shipment Returned', shipment?.returned],
    ];
    return {
        headers: ['Metric', 'Count'],
        // Numbers stay numbers on this sheet, unlike the media sheet: these are counts a reader will
        // want to add up, and there is no volser-style value here for Excel to misread.
        rows: entries.filter(([, value]) => Number.isFinite(Number(value))).map(([label, value]) => [label, Number(value)]),
    };
}

/** Two digits, for a filename stamp that sorts chronologically as text. */
const pad = (value) => String(value).padStart(2, '0');

/**
 * Filename for the exported workbook, scoped and stamped.
 *
 * Scope in the name because the same button on the same card produces a different file on the
 * processing tab (one job) than on the dashboard (whatever the header selector points at), and a folder
 * of files all called 'media-report.xlsx' is a folder nobody can use.
 *
 * @param {object} scope - {cid, jobIds, allJobs} — `allJobs` when the ids are every job the portal has
 *        onboarded rather than a selection, so the name says 'all-jobs' instead of counting them. The two
 *        are the same list and a very different report, and the reader opening the file months later has
 *        only its name to tell them which they took.
 * @param {Date} [now] - Injectable so the name is testable.
 */
export function mediaReportFileName({cid, jobIds, allJobs = false} = {}, now = new Date()) {
    const ids = (Array.isArray(jobIds) ? jobIds : (jobIds === undefined || jobIds === null ? [] : [jobIds])).filter(id => id !== null && id !== undefined && id !== '');
    // One job names itself; several are counted rather than listed, because a dashboard selection can
    // hold dozens and a 300-character filename is not a filename.
    const scopePart = allJobs ? 'all-jobs'
        : ids.length === 1 ? `job-${ids[0]}` : ids.length > 1 ? `${ids.length}-jobs` : (cid ? `client-${cid}` : 'all-clients');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    return `media-report-${scopePart}-${stamp}.xlsx`;
}
